import React from 'react';
import type { ChangeEvent } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { JsonInput } from '@shared/components/JsonInput/JsonInput';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';

import { StructuredOutputSchemaEditor, StructuredOutputSchemaNode } from './schemaEditor';
import { StructuredOutputSchemaMode } from './useStructuredOutputSchemaDraft';

type StructuredOutputFormFieldsProps = {
  name: string;
  description: string;
  instructions: string;
  schema: StructuredOutputSchemaNode | null;
  schemaError: string | null;
  schemaMode: StructuredOutputSchemaMode;
  rawSchemaJson: string;
  canRecoverSchemaJson: boolean;
  canSwitchToVisualSchema: boolean;
  isSchemaJsonLocked: boolean;
  isSubmitting: boolean;
  topHelperText?: string;
  showNameValidation?: boolean;
  showDescriptionValidation?: boolean;
  showSchemaValidation?: boolean;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onInstructionsChange: (value: string) => void;
  onSchemaChange: (value: StructuredOutputSchemaNode) => void;
  onSchemaJsonChange: (value: string) => void;
  onOpenSchemaJson: () => void;
  onBackToVisualSchema: () => void;
  onFormatSchemaJson: () => void;
  onRevertSchemaJson: () => void;
  onNameBlur?: () => void;
  onDescriptionBlur?: () => void;
};

const StructuredOutputFormFields: React.FC<StructuredOutputFormFieldsProps> = ({
  name,
  description,
  instructions,
  schema,
  schemaError,
  schemaMode,
  rawSchemaJson,
  canRecoverSchemaJson,
  canSwitchToVisualSchema,
  isSchemaJsonLocked,
  isSubmitting,
  topHelperText,
  showNameValidation = false,
  showDescriptionValidation = false,
  showSchemaValidation = false,
  onNameChange,
  onDescriptionChange,
  onInstructionsChange,
  onSchemaChange,
  onSchemaJsonChange,
  onOpenSchemaJson,
  onBackToVisualSchema,
  onFormatSchemaJson,
  onRevertSchemaJson,
  onNameBlur,
  onDescriptionBlur,
}) => {
  const { t } = useTranslation();
  const isJsonMode = schemaMode === 'json';
  const shouldShowSchemaAlert = Boolean(schemaError);
  const schemaAlertDescription = isJsonMode
    ? t('campaignPage.callAnalysis.editor.schemaRepairDescription', { error: schemaError })
    : t('campaignPage.callAnalysis.editor.schemaErrorDescription', { error: schemaError });

  return (
    <>
      {topHelperText ? (
        <Typography variant="body2" color="text.secondary">
          {topHelperText}
        </Typography>
      ) : null}

      <Stack spacing={2}>
        <CustomTextField
          label={t('campaignPage.callAnalysis.editor.nameLabel')}
          value={name}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onNameChange(event.target.value)}
          onBlur={onNameBlur}
          disabled={isSubmitting}
          error={showNameValidation && !name.trim()}
          helperText={
            showNameValidation && !name.trim()
              ? t('campaignPage.callAnalysis.validation.nameRequired')
              : ' '
          }
        />

        <CustomTextField
          label={t('campaignPage.callAnalysis.editor.descriptionLabel')}
          value={description}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onDescriptionChange(event.target.value)
          }
          onBlur={onDescriptionBlur}
          disabled={isSubmitting}
          error={showDescriptionValidation && !description.trim()}
          helperText={
            showDescriptionValidation && !description.trim()
              ? t('campaignPage.callAnalysis.validation.descriptionRequired')
              : ' '
          }
        />

        <CustomTextField
          label={t('campaignPage.callAnalysis.editor.instructionsLabel')}
          value={instructions}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            onInstructionsChange(event.target.value)
          }
          disabled={isSubmitting}
          helperText={t('campaignPage.callAnalysis.editor.instructionsHelper')}
          multiline
          minRows={4}
        />
      </Stack>

      {shouldShowSchemaAlert ? (
        <Alert severity="error">
          <Box>
            <Typography variant="subtitle2">
              {isJsonMode
                ? t('campaignPage.callAnalysis.editor.schemaRepairTitle')
                : t('campaignPage.callAnalysis.editor.schemaErrorTitle')}
            </Typography>
            <Typography variant="body2">{schemaAlertDescription}</Typography>
          </Box>
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          {isJsonMode ? (
            <>
              <Button
                size="small"
                variant="text"
                disabled={isSubmitting}
                onClick={onFormatSchemaJson}>
                {t('campaignPage.callAnalysis.editor.formatJson')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={isSubmitting || !canRecoverSchemaJson}
                onClick={onRevertSchemaJson}>
                {t('campaignPage.callAnalysis.editor.revertToLastValid')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={isSubmitting || !canSwitchToVisualSchema}
                onClick={onBackToVisualSchema}>
                {t('campaignPage.callAnalysis.editor.backToVisualEditor')}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="outlined"
              disabled={isSubmitting}
              onClick={onOpenSchemaJson}>
              {t('campaignPage.callAnalysis.editor.editJson')}
            </Button>
          )}
        </Stack>

        {isJsonMode ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {isSchemaJsonLocked
                ? t('campaignPage.callAnalysis.editor.schemaJsonLockedHelper')
                : t('campaignPage.callAnalysis.editor.schemaJsonModeHelper')}
            </Typography>
            <JsonInput
              rows={18}
              value={rawSchemaJson}
              onChange={onSchemaJsonChange}
              disabled={isSubmitting}
              error={schemaError}
              placeholder={t('campaignPage.callAnalysis.editor.schemaJsonPlaceholder')}
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: 360,
                  alignItems: 'stretch',
                },
                '& .MuiInputBase-inputMultiline': {
                  height: '100% !important',
                  overflow: 'auto !important',
                },
              }}
            />
          </Stack>
        ) : schema ? (
          <StructuredOutputSchemaEditor
            value={schema}
            onChange={onSchemaChange}
            error={schemaError}
            disabled={isSubmitting}
            showValidation={showSchemaValidation}
          />
        ) : null}
      </Stack>
    </>
  );
};

export default StructuredOutputFormFields;
